import { useCallback } from 'react';

interface UseSectionInteractionHandlersParams {
  enabledSensors: Record<string, Record<string, boolean>>;
  enableSensor: (section: string, name: string) => void;
  disableSensor: (section: string, name: string) => void;
  toggleSensor: (section: string, name: string) => void;
  clearSensors: (section: string) => void;
  resetSensors: (section: string) => void;
  resetFeatures: (section: string) => void;
  sectionConfigGraphs: any[];
  syncSectionLegends?: boolean;
  focusMode?: boolean;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  cancelSectionRequest: (section: string) => void;
}

export const useSectionInteractionHandlers = ({
  enabledSensors,
  enableSensor,
  disableSensor,
  toggleSensor,
  clearSensors,
  resetSensors,
  resetFeatures,
  sectionConfigGraphs,
  syncSectionLegends,
  focusMode,
  onFocus,
  cancelSectionRequest,
}: UseSectionInteractionHandlersParams) => {
  const handleToggleSensor = useCallback(
    (sectionType: string, sensorName: string) => {
      const allSectionTypes: string[] = sectionConfigGraphs.map((g: any) => g.id);

      const currentState = enabledSensors[sectionType]?.[sensorName];
      const newState = !currentState;

      // Toggling sensor in section/sections
      if (syncSectionLegends) {
        if (focusMode) {
          // Focus mode: Always focus the clicked sensor

          // If sensor is disabled, enable it first (will trigger data fetch)
          if (!currentState) {
            allSectionTypes.forEach((section: string) => {
              // Only enable if the sensor exists in this section
              if (enabledSensors[section] && sensorName in enabledSensors[section]) {
                enableSensor(section, sensorName);
              }
            });
          }

          // Call onFocus to set this sensor as focused
          // This will trigger the focus logic in the parent component
          if (onFocus) {
            onFocus(sensorName, sectionType);
          }
        } else {
          // Multi-select mode: Sync the toggle across all sections

          allSectionTypes.forEach((section: string) => {
            // Check if this sensor exists in this section
            if (enabledSensors[section] && sensorName in enabledSensors[section]) {
              // Set the sensor to the target state
              if (newState) {
                enableSensor(section, sensorName);
              } else {
                disableSensor(section, sensorName); // Use disableSensor to explicitly disable
              }
            }
          });
        }
      } else {
        // For single section (no sync)
        if (focusMode) {
          // Focus mode: Always focus the clicked sensor

          // If sensor is disabled, enable it first (will trigger data fetch)
          if (!currentState) {
            enableSensor(sectionType, sensorName);
          }

          // Call onFocus to set this sensor as focused
          if (onFocus) {
            onFocus(sensorName, sectionType);
          }
        } else {
          // Normal toggle mode
          if (newState) {
            enableSensor(sectionType, sensorName);
          } else {
            disableSensor(sectionType, sensorName); // Use disableSensor to explicitly disable
          }
        }
      }
    },
    [
      enabledSensors,
      enableSensor,
      disableSensor,
      toggleSensor,
      clearSensors,
      sectionConfigGraphs,
      syncSectionLegends,
      focusMode,
      onFocus,
    ]
  );

  const handleReset = useCallback(
    async (sectionType: string) => {
      const allSectionTypes: string[] = sectionConfigGraphs.map((g: any) => g.id);
      try {
        cancelSectionRequest(sectionType);

        await new Promise((resolve) => setTimeout(resolve, 50));

        // Only restore state, don't trigger re-fetch
        // UI will re-render because enabledSensors changed
        if (syncSectionLegends) {
          allSectionTypes.forEach((sectionType: string) => {
            resetSensors(sectionType);
          });
        } else {
          resetSensors(sectionType);
        }

        // we only reset the legend
        // resetFeatures(sectionType);
      } catch (error) {}
    },
    [resetSensors, resetFeatures, cancelSectionRequest, enabledSensors, sectionConfigGraphs, syncSectionLegends]
  );

  const handleClearAll = useCallback(
    async (sectionType: string) => {
      const allSectionTypes: string[] = sectionConfigGraphs.map((g: any) => g.id);
      try {
        cancelSectionRequest(sectionType);
        await new Promise((resolve) => setTimeout(resolve, 50));
        if (syncSectionLegends) {
          allSectionTypes.forEach((sectionType: string) => {
            clearSensors(sectionType);
          });
        } else {
          clearSensors(sectionType);
        }
      } catch (error) {}
    },
    [clearSensors, cancelSectionRequest, sectionConfigGraphs, syncSectionLegends]
  );

  return {
    handleToggleSensor,
    handleReset,
    handleClearAll,
  };
};
